/**
 * What the plan was built from, answer by answer.
 *
 * Speak ($3.25M/mo, the category's largest) and Lingo AI both replay the learner's own
 * quiz answers back as a plan before asking for anything, and ScreensDesign scores it 8/10
 * and 7/10 for originality (docs/research/16-category-leaders-2026-10-20.md §4). The
 * mechanism is not decoration: a learner who answered nine questions wants to see that the
 * answers went somewhere, and "personalized" is a claim until the derivation is shown.
 *
 * Our `PlanStep` already named the level and the daily minutes. What it never did was
 * connect an answer to a consequence, so the nine steps before it were, from the learner's
 * side, unaccounted for.
 *
 * The one rule that matters here: **a row exists only when the learner actually answered**.
 * No defaults, no "we assumed", no filler to make the list look longer. An invented row
 * would be the same dishonesty as a projected date with nothing behind it
 * (`projectProgress`), and it is easier to introduce, because a missing answer has an
 * obvious-looking fallback.
 */

import type { PriorAppFailure } from './prior-app.js';

export interface PlanBasisInput {
  /** The estimate after placement, or null when the probe was skipped. */
  readonly level: string | null;
  /** The dialect they chose, already labelled for display. Null for one-dialect languages. */
  readonly dialectLabel: string | null;
  readonly dailyGoalMinutes: number;
  /** Goal ids from the motivation step. Empty when they skipped it. */
  readonly goals: readonly string[];
  /** What went wrong with the app they tried before, or null if they did not say. */
  readonly priorAppFailure: PriorAppFailure | null;
  /** How many words they ticked in the placement probe. */
  readonly knownWordCount: number;
  /** Which mode the first lesson opens in — text when they chose it after A6b. */
  readonly preferredMode: 'voice' | 'text';
  /** The tutor they picked, by name. */
  readonly personaName: string | null;
}

export type PlanBasisId =
  | 'level'
  | 'dialect'
  | 'minutes'
  | 'goals'
  | 'prior_app'
  | 'words'
  | 'mode'
  | 'tutor';

export interface PlanBasisRow {
  readonly id: PlanBasisId;
  /** What the learner told us, in their terms. */
  readonly youSaid: string;
  /** What it changed about the plan — a mechanism, never a restatement of `youSaid`. */
  readonly soWe: string;
}

const GOAL_LABELS: Readonly<Record<string, string>> = {
  travel: 'travel',
  work: 'work',
  family: 'family and relationships',
  exam: 'an exam',
  moving: 'moving abroad',
  curiosity: 'curiosity',
};

/** What each prior-app complaint changed, by the mechanism that answers it. */
const PRIOR_APP_CONSEQUENCE: Readonly<Record<PriorAppFailure, string>> = {
  wanted_money_first: 'put your ten free speaking minutes a day before any paywall',
  marked_me_wrong: 'withhold a score when we are not sure, instead of guessing one',
  never_spoke: 'start you speaking in lesson one, not after a vocabulary course',
  lost_interest: 'keep sessions to the length you chose and pick the next thing for you',
  too_easy: 'aim content one sub-level above where you tested',
  too_hard: 'scaffold the first turns and let you answer in text whenever you want',
  other: 'show you how every number on your progress screen was worked out',
};

/** English list joining: "a", "a and b", "a, b and c". */
function listify(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The plan's derivation, in the order the learner gave the answers.
 *
 * Returns an empty list when they answered nothing we can account for, which is a real
 * state — a learner who skipped the probe and the quiz has told us only their language —
 * and the UI shows no section rather than an empty heading.
 */
export function planBasis(input: PlanBasisInput): readonly PlanBasisRow[] {
  const rows: PlanBasisRow[] = [];

  if (input.dialectLabel !== null && input.dialectLabel !== '') {
    rows.push({
      id: 'dialect',
      youSaid: `You picked ${input.dialectLabel}`,
      soWe: 'teach that dialect, and use its voice — not a neutral one nobody speaks',
    });
  }

  // The probe before the level, because ticking the words is what produced the level, and
  // showing the estimate first makes the ticking look decorative.
  if (input.knownWordCount > 0) {
    rows.push({
      id: 'words',
      youSaid: `You knew ${input.knownWordCount} of the words we showed you`,
      soWe: 'skip those in your deck and start you on the ones you did not tick',
    });
  }

  if (input.level !== null && input.level !== '') {
    rows.push({
      id: 'level',
      youSaid: `You tested at ${input.level}`,
      soWe: 'pitch lessons one sub-level above that, and re-check it as you speak',
    });
  }

  if (input.goals.length > 0) {
    const labels = input.goals.map((goal) => GOAL_LABELS[goal] ?? goal);
    rows.push({
      id: 'goals',
      youSaid: `You are learning for ${listify(labels)}`,
      soWe: 'choose role-plays from those situations before any others',
    });
  }

  if (input.priorAppFailure !== null) {
    rows.push({
      id: 'prior_app',
      youSaid: 'You told us what went wrong with your last app',
      soWe: PRIOR_APP_CONSEQUENCE[input.priorAppFailure],
    });
  }

  if (input.personaName !== null && input.personaName !== '') {
    rows.push({
      id: 'tutor',
      youSaid: `You chose ${input.personaName}`,
      soWe: 'keep that voice and correction style across every session',
    });
  }

  // Only when they chose it: voice is the default, and listing a default as a decision
  // the learner made would be the invented row this function exists to avoid.
  if (input.preferredMode === 'text') {
    rows.push({
      id: 'mode',
      youSaid: 'You chose to type rather than speak first',
      soWe: 'open lesson one in text, and leave speaking there when you want it',
    });
  }

  if (input.dailyGoalMinutes > 0) {
    rows.push({
      id: 'minutes',
      youSaid: `You committed to ${input.dailyGoalMinutes} minutes a day`,
      soWe: 'size every session to fit that, and never run over it',
    });
  }

  return rows;
}
